import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { DatePipe } from '@angular/common';
import { RouterLink } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { Organisation } from '../../models/server/Organisation';
import { User } from '../../models/server/User';

/**
 * Tells people when their organisation is read-only because its subscription ended or a payment failed
 * (#1914), or is about to be because the grace period is running, and gives the subscriber a way back.
 * What is blocked is decided by the server; this only explains it.
 */
@Component({
  selector: 'app-read-only-banner',
  templateUrl: './read-only-banner.component.html',
  styleUrls: ['./read-only-banner.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [DatePipe, RouterLink, MatIconModule, MatButtonModule]
})
export class ReadOnlyBannerComponent {

  readonly organisation = input<Organisation | null>(null);
  readonly user = input<User | null>(null);

  /** 'readonly' once it has happened, 'grace' while a failed payment can still be fixed, otherwise nothing. */
  readonly state = computed<'readonly' | 'grace' | null>(() => {
    const org = this.organisation();
    if (!org) {
      return null;
    }
    if (org.isReadOnly) {
      return 'readonly';
    }
    return org.isInGracePeriod ? 'grace' : null;
  });

  readonly isSubscriber = computed(() => !!this.user()?.isSubscriber);

  /** The subscriber paused their subscription (#1883); resuming (or waiting for it to end) restores access. */
  readonly isPaused = computed(() => this.organisation()?.readOnlyReason === 'paused');

  /** Billing (as opposed to support) is why it is read-only, so subscribing will put it right. */
  readonly canResubscribe = computed(() => {
    const reason = this.organisation()?.readOnlyReason;
    return reason === 'ended' || reason === 'nonpayment';
  });
}
